/**
 * OpenAI 兼容的流式对话（与 iOS QwenChat 一致）：浏览器直接请求百炼 / DeepSeek / 自定义服务，SSE 流式 + 工具调用。
 * 不经过我们的服务端（用户要求）。
 */
import { buildChatBody, providerError, type ChatMessage, type ChatProvider, type ToolSpec } from "./providers";

export interface ToolCall {
  id: string;
  name: string;
  arguments: string;
}

export interface Turn {
  content: string;
  reasoning: string;
  toolCalls: ToolCall[];
}

export class ChatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChatError";
  }
}

/** 把一个个 SSE 数据块累加成完整的一轮（纯逻辑，方便测试） */
export class TurnAccumulator {
  content = "";
  reasoning = "";
  private calls = new Map<number, ToolCall>();

  /** 处理一个 data: 事件的 JSON，返回这次的思考增量和文字增量 */
  push(obj: unknown): { reasoning: string; text: string } {
    const out = { reasoning: "", text: "" };
    if (!obj || typeof obj !== "object") return out;
    const o = obj as { error?: { message?: string }; choices?: { delta?: Record<string, unknown> }[] };
    if (o.error) throw new ChatError(`对话出错：${o.error.message ?? "未知错误"}`);
    const delta = o.choices?.[0]?.delta;
    if (!delta) return out;
    if (typeof delta.reasoning_content === "string" && delta.reasoning_content) {
      this.reasoning += delta.reasoning_content;
      out.reasoning = delta.reasoning_content;
    }
    if (typeof delta.content === "string" && delta.content) {
      this.content += delta.content;
      out.text = delta.content;
    }
    const tcs = Array.isArray(delta.tool_calls) ? (delta.tool_calls as Record<string, unknown>[]) : [];
    for (const tc of tcs) {
      const index = typeof tc.index === "number" ? tc.index : 0;
      const call = this.calls.get(index) ?? { id: "", name: "", arguments: "" };
      if (typeof tc.id === "string" && tc.id) call.id = tc.id;
      const fn = tc.function as { name?: string; arguments?: string } | undefined;
      if (fn) {
        if (typeof fn.name === "string" && fn.name) call.name = fn.name;
        if (typeof fn.arguments === "string") call.arguments += fn.arguments;
      }
      this.calls.set(index, call);
    }
    return out;
  }

  result(): Turn {
    const toolCalls = [...this.calls.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, c]) => c)
      .filter((c) => c.name);
    // 个别服务不给 id：补一个，保证工具结果能对上
    toolCalls.forEach((c, i) => {
      if (!c.id) c.id = `call_${Date.now().toString(36)}_${i}`;
    });
    return { content: this.content, reasoning: this.reasoning, toolCalls };
  }
}

export interface StreamOptions {
  provider: ChatProvider;
  apiKey: string;
  model: string;
  messages: ChatMessage[];
  tools?: ToolSpec[];
  /** 思考等级 off / low / medium / high；null 为这一步不思考 */
  thinking: string | null;
  signal?: AbortSignal;
  onReasoning?: (delta: string) => void;
  onText?: (delta: string) => void;
}

/** 流式请求一轮对话；结束后返回完整内容和工具调用 */
export async function streamChat(opts: StreamOptions): Promise<Turn> {
  const body = buildChatBody({ provider: opts.provider, model: opts.model, messages: opts.messages, tools: opts.tools ?? [], thinking: opts.thinking });
  let res: Response;
  try {
    res = await fetch(opts.provider.chatUrl, {
      method: "POST",
      headers: { Authorization: `Bearer ${opts.apiKey}`, "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw e;
    throw new ChatError(`连不上${opts.provider.name}，请检查网络`);
  }
  if (res.status !== 200) {
    const text = await res.text().catch(() => "");
    throw new ChatError(providerError(opts.provider, res.status, text));
  }
  if (!res.body) throw new ChatError(`${opts.provider.name}没有返回内容`);

  const acc = new TurnAccumulator();
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = "";
  let done = false;
  try {
    while (!done) {
      const r = await reader.read();
      if (r.done) break;
      buf += r.value;
      let nl: number;
      while ((nl = buf.indexOf("\n")) >= 0) {
        const line = buf.slice(0, nl).replace(/\r$/, "");
        buf = buf.slice(nl + 1);
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (payload === "[DONE]") {
          done = true;
          break;
        }
        let obj: unknown;
        try {
          obj = JSON.parse(payload);
        } catch {
          continue;
        }
        const d = acc.push(obj);
        if (d.reasoning) opts.onReasoning?.(d.reasoning);
        if (d.text) opts.onText?.(d.text);
      }
    }
  } catch (e) {
    if ((e as Error)?.name === "AbortError" || e instanceof ChatError) throw e;
    throw new ChatError(`${opts.provider.name}的连接中断了，请重试`);
  } finally {
    reader.cancel().catch(() => undefined);
  }
  return acc.result();
}
